class TripPDFService {
  constructor() {
    this.pdfProvider = import.meta.env?.VITE_PDF_PROVIDER || 'jspdf';
  }

  async generatePDF(tripData) {
    try {
      switch (this.pdfProvider) {
        case 'react-pdf':
          return await this.generateReactPDF(tripData);
        case 'html2pdf':
          return await this.generateHtml2PDF(tripData);
        case 'jspdf':
        default:
          return await this.generateJsPDF(tripData);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF');
    }
  }

  // ✅ NEW: Visual snapshot generation using html2canvas
  async generateVisualSnapshotPDF(elementId = 'trip-detail-content') {
    try {
      // Dynamically import required libraries
      const [html2canvasModule, jsPDFModule] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      const html2canvas = html2canvasModule?.default;
      const jsPDF = jsPDFModule?.default;

      // Find the trip detail content element
      const element = document?.getElementById(elementId) || document?.querySelector('.trip-detail-container');
      
      if (!element) {
        throw new Error('Trip detail content not found. Please ensure you are on the trip details page.');
      }

      // ✅ ENHANCED: Capture with optimized settings for better page breaks
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true, // Handle external images
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element?.scrollWidth,
        windowHeight: element?.scrollHeight,
        scrollY: -window?.scrollY,
        scrollX: -window?.scrollX,
        allowTaint: true,
        removeContainer: true,
        // ✅ NEW: Improve rendering quality
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          // ✅ CRITICAL: Ensure all content is visible in cloned document
          const clonedElement = clonedDoc?.getElementById(elementId) || clonedDoc?.querySelector('.trip-detail-container');
          if (clonedElement) {
            clonedElement.style.height = 'auto';
            clonedElement.style.maxHeight = 'none';
            clonedElement.style.overflow = 'visible';
          }
        }
      });

      // Convert canvas to image data
      const imgData = canvas?.toDataURL('image/png', 1.0);
      
      // Calculate PDF dimensions to fit the content
      const imgWidth = canvas?.width;
      const imgHeight = canvas?.height;
      
      // A4 dimensions in pixels at 72 DPI
      const pdfWidth = 595.28; // A4 width in points
      const pdfHeight = 841.89; // A4 height in points
      
      // Calculate scale to fit content in PDF width
      const scale = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * scale;
      
      // ✅ ENHANCED: Create PDF with intelligent page breaking
      const pdf = new jsPDF('p', 'pt', 'a4');
      
      if (scaledHeight > pdfHeight) {
        // Content spans multiple pages - use smart page breaks
        let yOffset = 0;
        let pageNumber = 1;
        
        // ✅ CRITICAL: Improved page break logic to prevent cutting content
        const margin = 40; // Leave margin at top/bottom of each page
        const usablePageHeight = pdfHeight - (margin * 2); // Account for margins
        
        while (yOffset < scaledHeight) {
          if (pageNumber > 1) {
            pdf?.addPage();
          }
          
          // ✅ ENHANCED: Add content with proper margins
          pdf?.addImage(
            imgData,
            'PNG',
            0,
            margin - yOffset, // Adjust position to account for margin
            pdfWidth,
            scaledHeight,
            undefined,
            'FAST'
          );
          
          yOffset += usablePageHeight;
          pageNumber++;
        }
      } else {
        // Content fits in one page
        pdf?.addImage(
          imgData,
          'PNG',
          0,
          0,
          pdfWidth,
          scaledHeight,
          undefined,
          'FAST'
        );
      }
      
      // Add footer to each page
      const totalPages = pdf?.internal?.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf?.setPage(i);
        pdf?.setFontSize(8);
        pdf?.setTextColor(128, 128, 128);
        
        // Bottom center - Page number
        pdf?.text(
          `Page ${i} of ${totalPages}`,
          pdfWidth / 2,
          pdfHeight - 20,
          { align: 'center' }
        );
        
        // Bottom left - Generated by
        pdf?.text(
          'Generated by ParkTrip Planner AI',
          20,
          pdfHeight - 20
        );
        
        // Bottom right - Date
        pdf?.text(
          new Date()?.toLocaleDateString(),
          pdfWidth - 20,
          pdfHeight - 20,
          { align: 'right' }
        );
      }

      // Return as blob
      return pdf?.output('blob');
    } catch (error) {
      console.error('Error with visual snapshot generation:', error);
      throw new Error(`Visual snapshot failed: ${error?.message}`);
    }
  }

  async generateJsPDF(tripData) {
    try {
      // Dynamically import jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      let yPosition = 20;
      const lineHeight = 10;
      const margin = 20;
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();

      // ✅ ENHANCED: Title with destination
      doc?.setFontSize(22);
      doc?.setFont(undefined, 'bold');
      const title = tripData?.title || `${tripData?.destination || tripData?.park} Adventure`;
      doc?.text(title, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += lineHeight * 1.5;

      // ✅ NEW: Subtitle with generation date
      doc?.setFontSize(10);
      doc?.setFont(undefined, 'normal');
      doc?.text(`Generated on ${new Date()?.toLocaleDateString()} by ParkTrip Planner AI`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += lineHeight * 2;

      // ✅ ENHANCED: Trip Overview with more details
      doc?.setFontSize(14);
      doc?.setFont(undefined, 'bold');
      doc?.text('Trip Overview', margin, yPosition);
      yPosition += lineHeight;

      doc?.setFontSize(10);
      doc?.setFont(undefined, 'normal');
      const overviewItems = [
        `Destination: ${tripData?.destination || tripData?.park || 'Multiple Parks'}`,
        `Duration: ${tripData?.duration ? `${tripData?.duration} Days` : '3 Days'}`,
        `Travelers: ${tripData?.participants || tripData?.travelers || '2-4 People'}`,
        `Transportation: ${tripData?.transportation || 'Personal Vehicle'}`,
        `Accommodation: ${tripData?.accommodation || 'Camping'}`,
        `Estimated Cost: ${tripData?.estimatedCost || tripData?.total_cost || 'Moderate'}`,
        `Weather: ${tripData?.weatherForecast || 'Check local conditions'}`
      ];

      overviewItems?.forEach(item => {
        if (yPosition > pageHeight - 20) {
          doc?.addPage();
          yPosition = 20;
        }
        doc?.text(item, margin, yPosition);
        yPosition += lineHeight;
      });

      yPosition += lineHeight;

      // ✅ ENHANCED: Featured Parks with better formatting
      if (tripData?.featuredParks?.length > 0) {
        if (yPosition > pageHeight - 40) {
          doc?.addPage();
          yPosition = 20;
        }
        
        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Featured Parks', margin, yPosition);
        yPosition += lineHeight;

        doc?.setFontSize(10);
        doc?.setFont(undefined, 'normal');
        tripData?.featuredParks?.forEach(park => {
          if (yPosition > pageHeight - 20) {
            doc?.addPage();
            yPosition = 20;
          }
          doc?.text(`• ${park}`, margin + 5, yPosition);
          yPosition += lineHeight;
        });
        yPosition += lineHeight;
      }

      // ✅ ENHANCED: Activities with tags
      if (tripData?.activityTypes?.length > 0) {
        if (yPosition > pageHeight - 40) {
          doc?.addPage();
          yPosition = 20;
        }
        
        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Planned Activities', margin, yPosition);
        yPosition += lineHeight;

        doc?.setFontSize(10);
        doc?.setFont(undefined, 'normal');
        tripData?.activityTypes?.forEach(activity => {
          if (yPosition > pageHeight - 20) {
            doc?.addPage();
            yPosition = 20;
          }
          doc?.text(`• ${activity}`, margin + 5, yPosition);
          yPosition += lineHeight;
        });
        yPosition += lineHeight;
      }

      // ✅ CRITICAL FIX: Daily Itinerary - Ensure ALL days are rendered
      if (tripData?.days?.length > 0) {
        // Add new page for itinerary section if needed
        if (yPosition > pageHeight - 80) {
          doc?.addPage();
          yPosition = 20;
        }
        
        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Daily Itinerary', margin, yPosition);
        yPosition += lineHeight * 1.5;

        // ✅ CRITICAL: Process each day explicitly with proper pagination
        for (let dayIndex = 0; dayIndex < tripData?.days?.length; dayIndex++) {
          const day = tripData?.days?.[dayIndex];
          
          // Check if we need a new page for this day
          // Reserve more space (100 units) to ensure day header + at least one activity fits
          if (yPosition > pageHeight - 100) {
            doc?.addPage();
            yPosition = 20;
          }

          // Day Header
          doc?.setFontSize(12);
          doc?.setFont(undefined, 'bold');
          const dayTitle = `Day ${dayIndex + 1} - ${day?.title || day?.date || 'Activities'}`;
          doc?.text(dayTitle, margin, yPosition);
          yPosition += lineHeight;

          // Day Date (if available)
          if (day?.date) {
            doc?.setFontSize(10);
            doc?.setFont(undefined, 'italic');
            doc?.text(day?.date, margin, yPosition);
            yPosition += lineHeight * 0.8;
          }

          // Process activities for this day
          if (day?.activities?.length > 0) {
            doc?.setFontSize(10);
            doc?.setFont(undefined, 'normal');
            
            for (let actIndex = 0; actIndex < day?.activities?.length; actIndex++) {
              const activity = day?.activities?.[actIndex];
              
              // Check if we need a new page for this activity
              if (yPosition > pageHeight - 40) {
                doc?.addPage();
                yPosition = 20;
                
                // Re-add day context at top of new page
                doc?.setFontSize(10);
                doc?.setFont(undefined, 'italic');
                doc?.text(`${dayTitle} (continued)`, margin, yPosition);
                yPosition += lineHeight;
                doc?.setFont(undefined, 'normal');
              }

              // Activity header
              const activityText = `${activity?.time || ''} - ${activity?.title || activity?.activity || ''} ${activity?.location ? `at ${activity?.location}` : ''}`;
              
              // Handle text wrapping for activity header
              const splitText = doc?.splitTextToSize(activityText, pageWidth - margin * 2 - 10);
              splitText?.forEach(line => {
                if (yPosition > pageHeight - 20) {
                  doc?.addPage();
                  yPosition = 20;
                }
                doc?.text(line, margin + 5, yPosition);
                yPosition += lineHeight;
              });
              
              // Add description if available
              if (activity?.description) {
                doc?.setFont(undefined, 'italic');
                doc?.setFontSize(9);
                const descText = doc?.splitTextToSize(`   ${activity?.description}`, pageWidth - margin * 2 - 15);
                descText?.forEach(line => {
                  if (yPosition > pageHeight - 20) {
                    doc?.addPage();
                    yPosition = 20;
                  }
                  doc?.text(line, margin + 10, yPosition);
                  yPosition += lineHeight * 0.8;
                });
                doc?.setFontSize(10);
                doc?.setFont(undefined, 'normal');
              }
              
              // Add notes if available
              if (activity?.notes && activity?.notes !== activity?.description) {
                doc?.setFont(undefined, 'normal');
                doc?.setFontSize(9);
                const notesText = doc?.splitTextToSize(`   Notes: ${activity?.notes}`, pageWidth - margin * 2 - 15);
                notesText?.forEach(line => {
                  if (yPosition > pageHeight - 20) {
                    doc?.addPage();
                    yPosition = 20;
                  }
                  doc?.text(line, margin + 10, yPosition);
                  yPosition += lineHeight * 0.8;
                });
                doc?.setFontSize(10);
              }
            }
          } else {
            // No activities for this day
            doc?.setFontSize(10);
            doc?.setFont(undefined, 'italic');
            doc?.text('No activities scheduled', margin + 5, yPosition);
            yPosition += lineHeight;
            doc?.setFont(undefined, 'normal');
          }
          
          // Add spacing between days
          yPosition += lineHeight * 1.5;
        }
      }

      // ✅ NEW: Accommodation Information
      if (tripData?.accommodations?.campgrounds?.length > 0) {
        if (yPosition > pageHeight - 60) {
          doc?.addPage();
          yPosition = 20;
        }

        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Accommodation Options', margin, yPosition);
        yPosition += lineHeight;

        doc?.setFontSize(10);
        doc?.setFont(undefined, 'normal');
        tripData?.accommodations?.campgrounds?.slice(0, 3)?.forEach(campground => {
          if (yPosition > pageHeight - 25) {
            doc?.addPage();
            yPosition = 20;
          }
          doc?.setFont(undefined, 'bold');
          doc?.text(`• ${campground?.name}`, margin + 5, yPosition);
          yPosition += lineHeight * 0.8;
          doc?.setFont(undefined, 'normal');
          doc?.text(`  ${campground?.location} - ${campground?.cost}`, margin + 10, yPosition);
          yPosition += lineHeight;
        });
        yPosition += lineHeight;
      }

      // ✅ NEW: Dining Information
      if (tripData?.dining?.onSite?.length > 0) {
        if (yPosition > pageHeight - 60) {
          doc?.addPage();
          yPosition = 20;
        }

        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Dining Options', margin, yPosition);
        yPosition += lineHeight;

        doc?.setFontSize(10);
        doc?.setFont(undefined, 'normal');
        tripData?.dining?.onSite?.slice(0, 5)?.forEach(option => {
          if (yPosition > pageHeight - 20) {
            doc?.addPage();
            yPosition = 20;
          }
          doc?.text(`• ${option}`, margin + 5, yPosition);
          yPosition += lineHeight;
        });
        yPosition += lineHeight;
      }

      // ✅ ENHANCED: Highlights with better spacing
      if (tripData?.highlights?.length > 0) {
        if (yPosition > pageHeight - 80) {
          doc?.addPage();
          yPosition = 20;
        }

        doc?.setFontSize(14);
        doc?.setFont(undefined, 'bold');
        doc?.text('Trip Highlights', margin, yPosition);
        yPosition += lineHeight;

        doc?.setFontSize(10);
        doc?.setFont(undefined, 'normal');
        tripData?.highlights?.forEach(highlight => {
          if (yPosition > pageHeight - 20) {
            doc?.addPage();
            yPosition = 20;
          }
          const highlightText = doc?.splitTextToSize(`• ${highlight}`, pageWidth - margin * 2 - 10);
          highlightText?.forEach(line => {
            doc?.text(line, margin + 5, yPosition);
            yPosition += lineHeight;
          });
        });
      }

      // Footer
      const totalPages = doc?.internal?.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc?.setPage(i);
        doc?.setFontSize(8);
        doc?.setFont(undefined, 'normal');
        
        // Bottom left - Generated by info
        doc?.text(`Generated by ParkTrip Planner AI`, margin, pageHeight - 10);
        
        // Bottom center - Page number
        doc?.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        
        // Bottom right - Date
        doc?.text(`${new Date()?.toLocaleDateString()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      // Return as blob
      return doc?.output('blob');
    } catch (error) {
      console.error('Error with jsPDF generation:', error);
      throw new Error('PDF generation failed');
    }
  }

  async generateReactPDF(tripData) {
    try {
      // This would require @react-pdf/renderer
      // For now, fallback to jsPDF
      console.warn('React-PDF not implemented, falling back to jsPDF');
      return await this.generateJsPDF(tripData);
    } catch (error) {
      throw new Error('React-PDF generation failed');
    }
  }

  async generateHtml2PDF(tripData) {
    try {
      // This would require html2pdf.js
      // For now, fallback to jsPDF
      console.warn('HTML2PDF not implemented, falling back to jsPDF');
      return await this.generateJsPDF(tripData);
    } catch (error) {
      throw new Error('HTML2PDF generation failed');
    }
  }

  // Generate shareable trip summary (lighter version)
  async generateSummaryPDF(tripData) {
    try {
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      let yPosition = 30;
      const margin = 20;

      // Simple summary format
      doc?.setFontSize(18);
      doc?.setFont(undefined, 'bold');
      doc?.text('Trip Summary', margin, yPosition);
      yPosition += 20;

      doc?.setFontSize(12);
      doc?.setFont(undefined, 'normal');
      
      const summaryText = `
Destination: ${tripData?.featuredParks?.[0] || 'State Park Adventure'}
Duration: ${tripData?.duration || '3 Days'}
Activities: ${tripData?.activityTypes?.join(', ') || 'Outdoor Activities'}
Budget: ${tripData?.estimatedCost || 'Moderate'}

Generated by ParkTrip Planner AI
Visit www.parktripplannerai.com for more adventures
      `?.trim();

      const splitText = doc?.splitTextToSize(summaryText, 170);
      splitText?.forEach(line => {
        doc?.text(line, margin, yPosition);
        yPosition += 7;
      });

      return doc?.output('blob');
    } catch (error) {
      throw new Error('Summary PDF generation failed');
    }
  }
}

export const tripPDFService = new TripPDFService();